/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import { gnoment } from 'shared-ui/gnoment';
import axios from 'axios';
import {
    Spinner,
    SecurityHelper,
    ScrollToHelper,
    MediaGallery,
    Heading,
    DisplayHtmlContent,
    FlexCol,
    Accordion,
    ReadMoreBlock,
    Stack,
} from 'sarsaparilla';
import { withCompatRouter, COMPAT_ROUTER_PROP_TYPES } from 'sarsaparilla/legacy';

import {
    RatingSummaryContainer,
    CellSummaryContainer,
} from 'ui-rating-review/src/containers/AggregateBar';
import { getGatewayUrl } from 'shared-ui/utils/getGatewayUrl';
import { SharedVenuePage } from 'shared-ui/components/SharedVenuePage/SharedVenuePage';
import { AdditionalLinks } from 'shared-ui/components/SharedVenuePage/AdditionalLinks';
import withSoftReleaseCheck from 'shared-ui/containers/SoftReleaseWrapper';
import { SendGAEvent } from 'shared-ui/utils/gaCommon';
import { INVENTORY_PERMIT } from 'ui-field-sales/src/constants/types';

import { authModalActions } from 'shared-ui/auth/redux';
import VenueTabWrapper from '../../shared-components/venuePage/VenueTabWrapper';
import GettingHereShared from '../../shared-components/venuePage/GettingHereShared';
import PermitAvailability from '../components/venuePage/PermitAvailability';
import PermitLotteryDescription from '../components/venuePage/PermitLotteryDescription';
import PermitMap from '../shared/components/PermitMap';
import PermitVenueDates from '../../shared-components/venuePage/PermitVenueDates';
import Video from '../components/venuePage/Video';
import * as pActions from '../actions/permit';
import * as aActions from '../actions/availability';
import {
    getFirstDivision,
    getDivision,
    getCurrentTime,
    readRuleAsMoment,
    hasRule,
    errorsToStringList,
} from '../utils/utilsNew';

const defaultProps = {
    permit: {
        links: [],
        name: '',
    },
};
const propTypes = {
    router: COMPAT_ROUTER_PROP_TYPES,
    account: PropTypes.shape({}),
    dispatch: PropTypes.func,
    issuanceError: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            error: PropTypes.string,
        }),
    ]),
    error: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            error: PropTypes.string,
        }),
    ]),
    permit: PropTypes.shape({
        activities: PropTypes.array,
        addresses: PropTypes.array,
        description: PropTypes.shape({
            description: PropTypes.string,
            quota_details: PropTypes.string,
            season_information: PropTypes.string,
            lottery_information: PropTypes.string,
            need_to_know_permit: PropTypes.string,
            fee_policy: PropTypes.string,
            cancellation_policy: PropTypes.string,
            no_show_policy: PropTypes.string,
            discounts: PropTypes.string,
            driving_directions: PropTypes.string,
            edu_video_url: PropTypes.string,
            edu_video_title: PropTypes.string,
            video_url: PropTypes.string,
            video_title: PropTypes.string,
            gps_coordinates: PropTypes.string,
            video_transcript: PropTypes.string,
            edu_video_transcript: PropTypes.string,
        }),
        divisions: PropTypes.shape({}),
        id: PropTypes.string,
        latitude: PropTypes.number,
        lines: PropTypes.shape({}),
        links: PropTypes.array,
        longitude: PropTypes.number,
        lotteries: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string,
                is_active: PropTypes.bool,
            })
        ),
        lottery_fee: PropTypes.string,
        name: PropTypes.string,
        needToKnow: PropTypes.arrayOf(PropTypes.string),
        phone: PropTypes.string,
        points: PropTypes.shape({}),
        recarea: PropTypes.array,
        recreation_fee: PropTypes.string,
        reservation_fee: PropTypes.string,
        rules: PropTypes.array,
        time_zone: PropTypes.string,
    }),
    recareaError: PropTypes.string,
    userIssuances: PropTypes.arrayOf(PropTypes.shape({})),
    aggSeasonRules: PropTypes.array,
};

export class PermitVenuePage extends React.Component {
    static defaultProps = defaultProps;
    static displayName = 'PermitVenuePage';
    static propTypes = propTypes;

    constructor(props) {
        super(props);

        this.getDivision = this.getDivision.bind(this);
        this.handleDivisionSelection = this.handleDivisionSelection.bind(this);
        this.showTheLoginModal = this.showTheLoginModal.bind(this);
        this.handleImportantDatesClick = this.handleImportantDatesClick.bind(this);
        this.focusErrorNotification = this.focusErrorNotification.bind(this);

        this.state = {
            disableCalendar: true,
            activeTabIndex: 0,
            loadingUserIssuances: false,
            permitId: this.getPermitId(),
        };

        this.errorRef = React.createRef();
    }

    componentDidMount() {
        // clear availabilities in case availability data from another permit carries over
        this.props.dispatch(aActions.clearAvailabilitiesMap());

        if (this.props.router.location.pathname) {
            const permitId = this.getPermitId();

            axios
                .get(`/shared/permit/data/${permitId}/features.json`, {
                    credentials: 'include',
                })
                .then((json) => {
                    this.setState({
                        lines: json.data.lines,
                        points: json.data.points,
                        polygons: json.data.polygons,
                    });
                })
                .catch(() => {});

            const apiCalls = [
                this.props.dispatch(pActions.fetchPermit(permitId)),
                this.props.dispatch(pActions.fetchAggregateSeason(permitId)),
                this.props.dispatch(pActions.fetchLotteries(permitId)),
            ];
            Promise.all(apiCalls).then(() => {
                if (this.props.error || this.props.fetchError) {
                    // there was an error! be nice.
                    return;
                }

                const divisionId =
                    this.state?.division?.id || get(this.getDivision(), 'id', '');

                this.fetchAvailability(permitId, divisionId);

                // for permits with one division only, we need to set a division initially, otherwise we won't pass a
                // division id to the permit availability widget / permit registration page.
                this.setState({ division: this.props.permit.divisions[divisionId] });

                let recId = 0;

                if (this.props.permit.recareas[0]) {
                    recId = this.props.permit.recareas[0];
                    this.props.dispatch(pActions.fetchRecarea(recId));
                }
            });
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.permit.id && !isEqual(this.props.account, prevProps.account)) {
            if (!this.state.loadingUserIssuances) {
                this.setState({ loadingUserIssuances: true });
            }

            let divisionId = null;

            if (this.state.division && !isEmpty(this.state.division)) {
                divisionId = this.state.division.id;
            } else {
                const firstDivision = this.getDivision();
                divisionId = firstDivision.id;
            }

            this.fetchAvailability(this.props.permit.id, divisionId);
        }

        if (this.props.account && this.props.userIssuances) {
            this.setState({ loadingUserIssuances: false });
        }
    }

    componentWillUnmount() {
        this.props.dispatch(pActions.clearError());
    }

    getPermitId() {
        if (this.props.router.location.pathname) {
            return this.props.router.params.permitId;
        }

        return null;
    }

    getDivision() {
        if (this.state.division && !isEmpty(this.state.division)) {
            return this.state.division;
        }

        return getDivision(this.props.permit.divisions);
    }

    handleImportantDatesClick = () => {
        this.setState({ activeTabIndex: 0 }, () => {
            const el = document.getElementById('important-dates');
            const header = document.querySelector('header');
            const currentPosition = window.scrollY;
            const y =
                el.getBoundingClientRect().top - header.offsetHeight + currentPosition;
            ScrollToHelper.scrollToY(y);
            el.focus();
        });
    };

    handleOnActiveTabIndexChange = (newActiveTabIndex) => {
        this.setState({ activeTabIndex: newActiveTabIndex });
    };

    fetchAvailability(permitId, divisionId) {
        let _divisionId = divisionId;

        if (divisionId === '' || divisionId === undefined) {
            // if permit has multiple divisions, don't get any
            if (
                this.props.permit.divisions &&
                Object.keys(this.props.permit.divisions).length > 1
            ) {
                return;
            }

            // get the division if there's only 1 division for the permit
            const aDivision = getFirstDivision(this.props.permit.divisions);
            this.setState({ division: aDivision });
            _divisionId = aDivision?.id;
        }

        if (
            hasRule('SeasonEnd', this.props.aggSeasonRules, '') &&
            permitId !== '4098362'
        ) {
            this.props.dispatch(
                aActions.fetchAvailability(
                    permitId,
                    _divisionId,
                    gnoment().add(-2, 'day').startOf('day'),
                    readRuleAsMoment(
                        'SeasonEnd',
                        this.props.aggSeasonRules,
                        this.props.permit.time_zone,
                        '',
                        true
                    ),
                    SecurityHelper.isCommercialAccount()
                )
            );
        }
    }

    // handles the event of changing the selected division in the availability widget
    handleDivisionSelection(divisionId) {
        this.setState({ division: this.props.permit.divisions[divisionId] });

        if (divisionId === '') {
            this.setState({ disableCalendar: true });
        } else {
            this.setState({ disableCalendar: false });
        }

        this.fetchAvailability(this.props.permit.id, divisionId);
    }

    showTheLoginModal() {
        this.props.dispatch(authModalActions.openLoginModal());
    }

    tabsToShow = () => {
        return [
            {
                label: 'Overview',
                content: this.renderOverview(),
            },
            {
                label: 'Need to Know',
                content: this.renderNeedToKnow(),
            },
            {
                label: 'Fees & Cancellations',
                content: this.renderPolicy(),
            },
            {
                label: 'Getting Here',
                content: this.renderGettingHere(),
            },
            {
                label: 'Contacts',
                content: this.renderContacts(),
            },
        ];
    };

    focusErrorNotification() {
        if (this.errorRef.current) this.errorRef.current.focus();
    }

    formatPermitAddresses(addresses) {
        const formattedAddresses = [];

        let label = 'Mailing Address';

        if (addresses.length > 1) {
            label = 'Mailing Addresses';
        }

        for (const key in addresses) {
            if (Object.hasOwn(addresses, key)) {
                const address = addresses[key];

                const description1 = get(address, 'description1');
                const description2 = get(address, 'description2');
                const address1 = get(address, 'street_address1');
                const address2 = get(address, 'street_address2');
                const address3parts = [
                    get(address, 'city'),
                    get(address, 'state_code'),
                    get(address, 'postal_code'),
                ];
                let address3;
                if (address3parts[0] && address3parts[1] && address3parts[2]) {
                    address3 = `${address3parts[0]}, ${address3parts[1]} ${address3parts[2]}`;
                }
                const remarks = get(address, 'remarks');

                if (formattedAddresses.length > 0) {
                    label = '';
                }
                const phone = get(address, 'phone');

                formattedAddresses.push(
                    <div key={key} className="permit-venue-row">
                        <div className="permit-venue-left-col">
                            <Heading headingLevel={2} appearance="h5" className="mb-2">
                                {label}
                            </Heading>
                        </div>
                        <div className="permit-venue-right-col">
                            <div translate="no">
                                {description1 && <div>{description1}</div>}
                                {description2 && <div>{description2}</div>}
                                {address1 && <div>{address1}</div>}
                                {address2 && <div>{address2}</div>}
                                {address3 && <div>{address3}</div>}
                            </div>
                            {remarks && <div>{this.renderContent(remarks)}</div>}
                            {phone && (
                                <div>
                                    <strong>Phone</strong>: {phone}
                                </div>
                            )}
                        </div>
                    </div>
                );
            }
        }

        return formattedAddresses;
    }

    renderOverview() {
        const {
            description,
            season_information: seasonInformation,
            edu_video_url: eduVideoURL,
            edu_video_title: eduVideoTitle,
            video_url: videoURL,
            video_title: videoTitle,
            lottery_information: lotteryInformation,
            quota_details: quotaDetails,
            video_transcript: videoTranscript,
            edu_video_transcript: eduVideoTranscript,
        } = this.props.permit.description;

        return (
            <VenueTabWrapper permitId={this.props.permit.id}>
                <div className="content-section">
                    <ReadMoreBlock linesToShow={5}>
                        <DisplayHtmlContent html={description} />
                    </ReadMoreBlock>
                </div>

                {seasonInformation && (
                    <div className="content-section">
                        <Heading appearance="h5" headingLevel={2}>
                            Permit & Season Information
                        </Heading>
                        <DisplayHtmlContent html={seasonInformation} />
                    </div>
                )}

                <div className="content-section">
                    <PermitVenueDates permit={this.props.permit} />
                </div>

                {lotteryInformation && (
                    <div className="content-section">
                        <PermitLotteryDescription description={lotteryInformation} />
                    </div>
                )}

                {quotaDetails && (
                    <div className="content-section">
                        <Heading appearance="h5" headingLevel={2}>
                            How Does the Quota Work for this Permit?
                        </Heading>
                        <DisplayHtmlContent html={quotaDetails} />
                    </div>
                )}

                {((videoURL && videoTitle) || (eduVideoURL && eduVideoTitle)) && (
                    <div className="content-section">
                        {videoURL && (
                            <>
                                <Video title={videoTitle} url={videoURL} />
                                {videoTranscript && (
                                    <Accordion
                                        id="videoTranscript"
                                        items={[
                                            {
                                                heading: 'Video Transcript',
                                                content: (
                                                    <DisplayHtmlContent
                                                        html={videoTranscript}
                                                    />
                                                ),
                                            },
                                        ]}
                                    />
                                )}
                            </>
                        )}
                        {eduVideoURL && (
                            <>
                                <Video title={eduVideoTitle} url={eduVideoURL} />
                                {eduVideoTranscript && (
                                    <Accordion
                                        id="eduVideoTranscript"
                                        items={[
                                            {
                                                heading: 'Video Transcript',
                                                content: (
                                                    <DisplayHtmlContent
                                                        html={eduVideoTranscript}
                                                    />
                                                ),
                                            },
                                        ]}
                                    />
                                )}
                            </>
                        )}
                    </div>
                )}

                <MediaGallery assetId={this.getPermitId()} className="content-section" />
            </VenueTabWrapper>
        );
    }

    renderNeedToKnow() {
        const { need_to_know_permit: needToKnowPermit } = this.props.permit.description;
        return (
            <VenueTabWrapper permitId={this.state.permitId}>
                {this.renderContent(needToKnowPermit)}
            </VenueTabWrapper>
        );
    }

    renderContent(body, title) {
        if (body) {
            return (
                <>
                    {title && (
                        <Heading appearance="h5" headingLevel={2}>
                            {title}
                        </Heading>
                    )}
                    <DisplayHtmlContent html={body} />
                </>
            );
        }
        return null;
    }

    renderPolicy() {
        const {
            fee_policy: feePolicy,
            cancellation_policy: cancellationPolicy,
            no_show_policy: noShowPolicy,
            discounts,
        } = this.props.permit.description;

        return (
            <VenueTabWrapper permitId={this.state.permitId}>
                <div className="content-section">
                    {this.renderContent(feePolicy, 'Fee Policies')}
                </div>
                <div className="content-section">
                    {this.renderContent(
                        cancellationPolicy,
                        'Cancellation & Change Policies'
                    )}
                </div>
                <div className="content-section">
                    {this.renderContent(noShowPolicy, 'No Show Policies')}
                </div>
                <div className="content-section">
                    {this.renderContent(discounts, 'Discount Pass Policies')}
                </div>
            </VenueTabWrapper>
        );
    }

    renderGettingHere() {
        const { latitude, longitude } = this.props.permit;
        const { gps_coordinates: gpsCoordinates, driving_directions: drivingDirections } =
            this.props.permit.description;

        return (
            <VenueTabWrapper permitId={this.state.permitId}>
                <GettingHereShared
                    permitId={this.props.permit.id}
                    longitude={longitude}
                    latitude={latitude}
                    gpsCoordinates={gpsCoordinates}
                    drivingDirections={drivingDirections}
                    isLegacy
                />
            </VenueTabWrapper>
        );
    }

    renderContacts() {
        return (
            <VenueTabWrapper permitId={this.state.permitId}>
                {this.props.permit.addresses && (
                    <div>
                        {this.formatPermitAddresses(this.props.permit.addresses)}
                        {this.props.permit.phone && (
                            <div className="permit-venue-row">
                                <div className="permit-venue-left-col">
                                    <Heading
                                        headingLevel={2}
                                        appearance="h5"
                                        className="mb-2"
                                    >
                                        Phone Number
                                    </Heading>
                                </div>
                                <div className="permit-venue-right-col">
                                    <p>{this.props.permit.phone}</p>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </VenueTabWrapper>
        );
    }

    // RIGHT RAIL
    renderSide(isMobileApp = false) {
        const currentDate = getCurrentTime(this.props);
        const divisionId =
            this.state.division && this.state.division.id
                ? this.state.division.id
                : undefined;

        return (
            <section
                className="rec-venue-page-right-rail"
                aria-label="Permit Availability"
            >
                <Stack space="md">
                    <PermitAvailability
                        currentDate={currentDate}
                        hasTitle
                        rightRailOnly={isMobileApp}
                        handleImportantDatesClick={this.handleImportantDatesClick}
                        disableCalendar={this.state.disableCalendar}
                        divisionId={divisionId}
                        handleDivisionSelection={this.handleDivisionSelection}
                        showTheLoginModal={this.showTheLoginModal}
                        focusErrorNotification={this.focusErrorNotification}
                    />

                    {!isMobileApp && (
                        <>
                            {!isEmpty(this.props.permit.links) && (
                                <AdditionalLinks links={this.props.permit.links} />
                            )}
                        </>
                    )}
                </Stack>
            </section>
        );
    }

    render() {
        if (this.props.error || this.props.fetchError) {
            return (
                <div className="permit-content-outer-wrap">
                    <FlexCol
                        className="permit-availability-container"
                        md={7}
                        lg={8}
                        xsOrder={2}
                    >
                        An error occurred.
                    </FlexCol>
                </div>
            );
        }

        if (
            !this.props.permit ||
            this.props.router.params.permitId !== this.props.permit.id
        ) {
            return (
                <div className="permit-loading-wrapper">
                    <Spinner />
                </div>
            );
        }

        if (!this.props.permit.rules) {
            return <div />;
        }

        const ratingsPresent =
            get(this.props.ratingReviews, 'number_of_ratings', 0) !== 0;
        const cellCoveragePresent = !isNil(
            get(this.props.ratingReviews, 'aggregate_cell_coverage_ratings')
        );

        const path = [{ url: '/', title: 'Home' }];
        const recAreaName = get(this.props, 'recarea[0].name');
        const recAreaUrl = get(this.props, 'recarea[0].entity_id');

        let partOf = { title: recAreaName || '', url: '' };
        if (recAreaName && recAreaUrl) {
            partOf = { title: recAreaName, url: getGatewayUrl(recAreaUrl) };
            path.push({ url: getGatewayUrl(recAreaUrl), title: recAreaName });
        }
        path.push({ title: this.props.permit.name.trim() });

        const errorsCombined = [
            this.props.error,
            this.props.issuanceError,
            this.props.availabilityError,
        ];

        const errorsList = errorsToStringList(errorsCombined);

        return (
            <>
                {/* Fire the view_item GA Event */}
                <SendGAEvent
                    itemLevel="Facility"
                    itemCategory={INVENTORY_PERMIT}
                    facilityId={this.props.permit.id}
                    facilityName={this.props.permit.name}
                    isDataLoading={!this.props.permit || !this.props.permit.id}
                />
                <SharedVenuePage
                    map={
                        <div id="Mapbox" className="map">
                            <PermitMap
                                geoJsonCenterPoint={{
                                    geometry: {
                                        coordinates: [
                                            this.props.permit.longitude,
                                            this.props.permit.latitude,
                                        ],
                                        type: 'Point',
                                    },
                                    properties: {
                                        name: this.props.permit.name,
                                        type: 'Permit',
                                    },
                                    type: 'Feature',
                                }}
                                geoJsonLines={this.state.lines}
                                geoJsonPoints={this.state.points}
                                geoJsonPolygons={this.state.polygons}
                                mapContainer="Mapbox"
                            />
                        </div>
                    }
                    errors={errorsList.filter((err) => err)}
                    errorRef={this.errorRef}
                    title={this.props.permit.name}
                    assetId={this.props.permit.id}
                    favoriteEntityId={this.props.permit.id}
                    favoriteEntityType={'permit'}
                    inventoryType="Permit"
                    breadcrumbPath={path}
                    partOf={partOf}
                    starRating={
                        ratingsPresent && (
                            <RatingSummaryContainer
                                locationType="Permit"
                                locationId={this.props.permit.id}
                            />
                        )
                    }
                    cellRating={
                        cellCoveragePresent && (
                            <CellSummaryContainer
                                locationType="Permit"
                                locationId={this.props.permit.id}
                            />
                        )
                    }
                    tabs={this.tabsToShow()}
                    activeTabIndex={this.state.activeTabIndex}
                    onActiveTabIndexChange={this.handleOnActiveTabIndexChange}
                    mapLatitude={this.props.permit.latitude}
                    mapLongitude={this.props.permit.longitude}
                    hasCustomRightRail
                    rightRailCustom={
                        <div className="rec-venue-page-right-rail">
                            <Stack space="md">
                                {this.renderSide(SecurityHelper.isMobileApp())}
                            </Stack>
                        </div>
                    }
                />
            </>
        );
    }
}

function select(state) {
    return {
        account: state.auth.account,
        error: state.permit.error,
        issuanceError: state.permit.issuanceError,
        availabilityError: state.permit.availability_err,
        fetchError: state.permit.fetch_err,
        ld: state.ld,
        navigation: state.navigation,
        permit: state.permit.result,
        recarea: state.permit.recarea,
        recareaError: state.permit.recarea_err,
        aggSeasonRules: state.permit.agg_season_rules,
        doUserCheck: state.permit?.result?.is_inactive,
        softReleaseUserIdsFlagName: 'permitsSoftReleaseUserIds',
        ratingReviews: state.ratingReviews.fetchAggregate.data,
    };
}

export default withCompatRouter(connect(select)(withSoftReleaseCheck(PermitVenuePage)));

// cSpell:ignore recarea, recareas, mapbox, sarsa's
